import { useEffect, useState } from "react";
import api from "../services/api";

/**
 * Which modules are live.
 *
 * The parent-payments module is switched on by the platform, not by a club, so
 * every screen that belongs to it asks this before rendering anything. The
 * answer is fetched once per page load and shared: a dozen components asking
 * the same question should not be a dozen requests.
 *
 * While the answer is unknown, everything reads as OFF. A screen that flashes
 * a payment button and then takes it away is worse than one that appears a
 * moment later — and a family must never be offered a payment that the
 * platform has not turned on.
 */

export type Features = {
  paymentsModule: boolean;
  paymentsOnline: boolean;
  paymentsReminders: boolean;
  paymentsAutoDebit: boolean;
  paymentsDiscounts: boolean;
  paymentsInstalments: boolean;
};

export const ALL_OFF: Features = {
  paymentsModule: false,
  paymentsOnline: false,
  paymentsReminders: false,
  paymentsAutoDebit: false,
  paymentsDiscounts: false,
  paymentsInstalments: false,
};

/** Shared across components, and re-fetched only on a full page load. */
let inFlight: Promise<Features> | null = null;
let known: Features | null = null;

export async function fetchFeatures(): Promise<Features> {
  if (known) return known;
  if (!inFlight) {
    inFlight = api
      .get<Features>("/platform-settings/features")
      .then(({ data }) => {
        known = { ...ALL_OFF, ...data };
        return known;
      })
      .catch(() => {
        // Not cached as "known": a failed request must not pin the app off
        // for the rest of the session.
        inFlight = null;
        return ALL_OFF;
      });
  }
  return inFlight;
}

/** Call after the platform admin saves the switches, so the UI follows. */
export function clearFeatureCache(): void {
  known = null;
  inFlight = null;
}

export function useFeatures(): { features: Features; loaded: boolean } {
  const [features, setFeatures] = useState<Features>(known || ALL_OFF);
  const [loaded, setLoaded] = useState(!!known);

  useEffect(() => {
    let alive = true;
    fetchFeatures().then((f) => {
      if (!alive) return;
      setFeatures(f);
      setLoaded(true);
    });
    return () => {
      alive = false;
    };
  }, []);

  return { features, loaded };
}
